import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import VideoBadge from "@/components/public/VideoBadge";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1200&q=80";
const filters = ["all", "Collectivités", "Entreprises", "Particuliers"];

export default function Realisations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/realisations", { params: filter !== "all" ? { category: filter } : {} }).then((r) => setItems(r.data));
  }, [filter]);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div className="pt-32 md:pt-40 pb-24 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <Overline>Nos réalisations</Overline>
            <h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest leading-[1.02]">Des projets qui racontent notre savoir-faire</h1>
            <p className="mt-5 text-lg text-stone leading-relaxed">Chaque réalisation est une étude de cas : le défi, notre solution et les résultats obtenus pour des collectivités, entreprises et particuliers.</p>
          </div>
          <div className="shrink-0">
            <p className="font-display text-6xl text-brand leading-none">{String(items.length).padStart(2, "0")}</p>
            <p className="text-xs uppercase tracking-[0.18em] text-stone mt-2">Projets présentés</p>
          </div>
        </Reveal>

        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 mb-12 border-y border-border py-5" data-testid="realisation-filters">
          {filters.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${filter === f ? "bg-forest text-cream" : "bg-white border border-border text-forest hover:border-brand hover:text-brand"}`}>
              {f === "all" ? "Tous les projets" : f}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-white py-24 text-center">
            <p className="text-stone">Aucune réalisation dans cette catégorie pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Featured */}
            {featured && (
              <Reveal>
                <Link to={`/realisations/${featured.slug}`} data-testid={`realisation-card-${featured.slug}`}
                  className="group grid lg:grid-cols-2 rounded-[28px] overflow-hidden bg-forest text-cream hover:shadow-2xl hover:shadow-forest/20 transition-all">
                  <div className="relative min-h-[320px] lg:min-h-[440px] overflow-hidden">
                    <img src={fileUrl(featured.cover_image) || FALLBACK} alt={featured.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-5 left-5 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand">{featured.category}</span>
                    <VideoBadge r={featured} />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-flora">Étude de cas à la une</span>
                    <h2 className="mt-3 font-display text-3xl lg:text-4xl leading-tight">{featured.title}</h2>
                    {featured.location && <p className="mt-2 text-cream/60 flex items-center gap-1.5"><MapPin size={15} />{featured.location}</p>}
                    <p className="mt-4 text-cream/80 leading-relaxed line-clamp-3">{featured.summary}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-semibold text-flora group-hover:gap-3 transition-all">Voir l'étude de cas <ArrowRight size={18} /></span>
                  </div>
                </Link>
              </Reveal>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((r, i) => (
                  <Reveal key={r.id} delay={(i % 3) * 0.08}>
                    <Link to={`/realisations/${r.slug}`} data-testid={`realisation-card-${r.slug}`}
                      className="group block h-full rounded-[22px] overflow-hidden bg-white border border-border hover:shadow-xl hover:shadow-forest/5 hover:-translate-y-1.5 transition-all">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img src={fileUrl(r.cover_image) || FALLBACK} alt={r.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="absolute top-4 left-4 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand">{r.category}</span>
                        <span className="absolute top-4 right-4 h-9 w-9 rounded-full bg-forest/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ArrowUpRight size={17} className="text-cream" /></span>
                        <VideoBadge r={r} />
                      </div>
                      <div className="p-6">
                        <h3 className="font-display text-2xl text-forest leading-tight">{r.title}</h3>
                        {r.location && <p className="text-sm text-stone flex items-center gap-1 mt-1"><MapPin size={13} />{r.location}</p>}
                        <p className="text-sm text-stone mt-2 line-clamp-2">{r.summary}</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
